import { useCallback, useEffect, useState } from "react";
import { FiPlus, FiEdit2 } from "react-icons/fi";
import { useStaticPages } from "./useHook";
import StaticPageModal from "./components/StaticPageModal";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import Loader from "../../../Utils/loader";

function StaticPages() {
  const { loading, getList, getAvailableUrls } = useStaticPages();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.StaticPages.CREATE);
  const canUpdate = hasPermission(SystemPermissions.StaticPages.UPDATE);

  const [items, setItems] = useState([]);
  const [availableUrls, setAvailableUrls] = useState([]);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    offset: 0,
    limit: 10,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchAvailableUrls = useCallback(async () => {
    try {
      const urls = await getAvailableUrls();
      setAvailableUrls(urls || []);
    } catch {
      setAvailableUrls([]);
    }
  }, [getAvailableUrls]);

  const fetchList = useCallback(async () => {
    const data = await getList({
      offset: pagination.offset,
      limit: pagination.limit,
    });
    setItems(data.items || []);
    setPagination((prev) => ({
      ...prev,
      totalCount: data.pagination?.totalCount || 0,
    }));
  }, [pagination.offset, pagination.limit, getList]);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchList(), fetchAvailableUrls()]);
  }, [fetchList, fetchAvailableUrls]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchAvailableUrls();
  }, [fetchAvailableUrls]);

  const openCreate = () => {
    if (availableUrls.length === 0) return;
    setEditData(null);
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setEditData(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const canShowAddButton = canCreate && availableUrls.length > 0;

  const totalPages =
    Math.ceil((pagination.totalCount || 0) / pagination.limit) || 1;
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  if (loading && items.length === 0) {
    return <Loader message="Loading static pages..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Static Pages</h2>
        {canShowAddButton && (
          <button
            type="button"
            onClick={openCreate}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Static Page SEO
          </button>
        )}
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow mt-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Page URL</th>
                <th className="table-th">Meta Title</th>
                <th className="table-th">Robots</th>
                <th className="table-th">OG Type</th>
                {canUpdate && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={canUpdate ? 5 : 4}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                items.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td font-mono text-xs text-blue-600">
                      {row.static_page_url}
                    </td>
                    <td className="table-td max-w-[280px] truncate">
                      {row.meta_title || "—"}
                    </td>
                    <td className="table-td">{row.robots || "—"}</td>
                    <td className="table-td">{row.og_type || "—"}</td>
                    {canUpdate && (
                      <td className="table-td">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="btn btn-edit text-white"
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-between bg-white rounded-lg pagination-shadow px-4 py-3 text-sm">
          <span className="text-gray-500">{pagination.totalCount} total</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() =>
                setPagination((p) => ({
                  ...p,
                  offset: Math.max(0, p.offset - p.limit),
                }))
              }
              className="px-3 py-1.5 rounded border disabled:opacity-40"
            >
              Prev
            </button>
            <span className="px-2 py-1.5 text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setPagination((p) => ({
                  ...p,
                  offset: p.offset + p.limit,
                }))
              }
              className="px-3 py-1.5 rounded border disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <StaticPageModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editData={editData}
        onSuccess={refreshData}
      />
    </div>
  );
}

export default StaticPages;
